import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Sort, UrlParameter } from '../../../../../../shared/utils/url.builder';
import { Pageable } from '../../../../../../shared/model/core/pageable.model';
import { Page } from '../../../../../../shared/model/core/page.model';
import { AbstractRepositoryV2 } from '../../abstractRepositoryV2';
import { AbstractFindManyService } from '../findmany/abstractFindMany.service';
import { AbstractDataServiceAdapter } from '../../dataservice/adapter/abstractDataService.adapter';

export abstract class AbstractFindAllService<StoredEntity, Entity> extends AbstractFindManyService<StoredEntity, Entity> {

    readonly entities$: Observable<Entity[]> = new BehaviorSubject([]);
    protected readonly ids: Observable<(number | string)[]> = new BehaviorSubject([]);

    protected constructor(service: AbstractRepositoryV2<StoredEntity>,
                          dataService: AbstractDataServiceAdapter<StoredEntity, Entity, any>,
                          parameters?: UrlParameter[]) {
        super(service, dataService, parameters);
    }

    public refresh(): Promise<Entity[]> {
        return this.findAll();
    }

    protected onEntityLoaded(entities: Entity[]) {

    }

    public findAll(): Promise<Entity[]> {
        const entities = [];
        this.setIds(entities);
        this.setError(undefined);
        this.setLoading(true);
        this.setEntities([]);

        return new Promise<Entity[]>(
            (resolve, reject) => {
                this.coreFindPaginatedByDataService(this.buildPageable(0), this.additionalParameters).toPromise()
                    .then(pagedEntities => {
                        const totalPage = pagedEntities.totalPages;
                        const promises: Promise<Page<Entity>>[] = [];

                        entities.push(...pagedEntities.content);

                        for (let i = 1; i < totalPage; i++) {
                            promises.push(
                                this.coreFindPaginatedByDataService(this.buildPageable(i), this.additionalParameters).toPromise());
                        }

                        if (promises.length > 0) {
                            Promise.all(promises)
                                .then((values: Page<Entity>[]) => {
                                    values.forEach(value => entities.push(...value.content));
                                    this.setEntities(entities);
                                    this.onEntityLoaded(entities);
                                    resolve(entities);
                                })

                                .catch((e) => {
                                    this.setError(e);
                                    reject(e);
                                })
                                .finally(() => this.setLoading(false));
                        } else {
                            this.setEntities(entities);
                            this.setLoading(false);
                            this.onEntityLoaded(entities);
                            resolve(entities);
                        }
                    }).catch((e) => {
                        this.setError(e);
                        this.setLoading(false);
                        reject(e);
                    });
            }
        );

    }

    protected buildPageable(page: number): Pageable {
        return Pageable.ofWithSort(page, AbstractFindAllService.MAX_PAGE_SIZE, this.sort);
    }

    protected setIds(entities: Entity[]) {
        (this.ids as BehaviorSubject<(number | string)[]>).next(entities.map(value => this.getEntityId(value)));
    }

    public getEntities(): Entity[] {
        return (this.entities$ as BehaviorSubject<Entity[]>).value;
    }

    private setEntities(entities: Entity[]) {
        return (this.entities$ as BehaviorSubject<Entity[]>).next(entities);
    }
    setParameters(parameters: UrlParameter[]) {
        super.parameters = parameters;
    }

    setAdditionalParameters(parameters: UrlParameter[]) {
        super.parameters = parameters;
    }
}
